import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { Store, Building2, Mail, Lock, Loader2, AlertCircle, Users } from "lucide-react";
import { loginReseller, loginMerchant, loginStaff } from "@/shared/api/auth";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function PortalLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("merchant");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isStaffLogin, setIsStaffLogin] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!email || !password) {
      setError(t("pleaseEnterBothEmailAndPassword"));
      return;
    }
    
    setLoading(true);
    
    try {
      let dashboardPath;
      
      if (isStaffLogin) {
        // Map tab to panel type for staff login
        const panelTypeMap = {
          reseller: 'RESELLER',
          merchant: 'MERCHANT',
        };
        await loginStaff(email, password, panelTypeMap[activeTab]);
      } else {
        // Regular login based on active tab
        switch (activeTab) {
          case 'reseller':
            await loginReseller(email, password);
            break;
          case 'merchant':
            await loginMerchant(email, password);
            break;
        }
      }
      
      // Navigate to appropriate dashboard
      const dashboardMap = {
        reseller: '/ResellerDashboard',
        merchant: '/MerchantDashboard',
      };
      
      navigate(dashboardMap[activeTab]);
    } catch (err) {
      setError(err.message || t("loginFailed"));
      setLoading(false);
    }
  };

  const tabConfig = [
    { 
      value: 'merchant', 
      label: t("merchants"), 
      icon: Building2, 
      color: 'from-amber-500 to-orange-600',
      bgColor: 'amber',
      description: t("merchantBusinessAccess")
    },
    { 
      value: 'reseller', 
      label: t("resellers"), 
      icon: Store, 
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'emerald',
      description: t("resellerPartnerAccess")
    },
  ];

  const currentTab = tabConfig.find(t => t.value === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <style>{`
        .login-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .glow-effect-violet {
          box-shadow: 0 0 60px rgba(139, 92, 246, 0.3);
        }
        .glow-effect-emerald {
          box-shadow: 0 0 60px rgba(16, 185, 129, 0.3);
        }
        .glow-effect-amber {
          box-shadow: 0 0 60px rgba(245, 158, 11, 0.3);
        }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.div
            key={activeTab}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${currentTab?.color} shadow-lg mb-4 glow-effect-${currentTab?.bgColor}`}
          >
            {currentTab && <currentTab.icon className="h-10 w-10 text-white" />}
          </motion.div>
          <h1 className="text-3xl font-bold text-white mb-2">Demo Gateway Portal</h1>
          <p className="text-slate-400">{currentTab?.description}</p>
        </div>

        {/* Login Card */}
        <Card className="login-card border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-white">{t("signInToPortal")}</CardTitle>
            <CardDescription className="text-slate-400">
              {t("selectAccountTypeAndEnterCredentials")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Account Type Tabs */}
            <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); setError(""); }} className="mb-6">
              <TabsList className="grid grid-cols-2 bg-slate-800/50 p-1">
                {tabConfig.map((tab) => (
                  <TabsTrigger 
                    key={tab.value}
                    value={tab.value}
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-slate-700 data-[state=active]:to-slate-600 data-[state=active]:text-white text-slate-400"
                  >
                    <tab.icon className="h-4 w-4 mr-1.5" />
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300">{t("emailAddress")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={`${activeTab}@demo-gateway.com`}
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500 focus:ring-slate-500/20"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t("password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500 focus:ring-slate-500/20"
                  />
                </div>
              </div>

              {/* Staff Login Toggle */}
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="staffLogin"
                  checked={isStaffLogin}
                  onChange={(e) => setIsStaffLogin(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500/20" 
                />
                <label htmlFor="staffLogin" className="flex items-center gap-2 text-slate-400 cursor-pointer text-sm">
                  <Users className="h-4 w-4" />
                  {t("iAmStaffMember")}
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r ${currentTab?.color} hover:opacity-90 text-white font-medium py-5 rounded-xl transition-all duration-200 shadow-lg`}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t("signInAs").replace("{type}", `${currentTab?.label}${isStaffLogin ? ` ${t("staff")}` : ''}`)
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-700/50 text-center">
              <p className="text-slate-500 text-sm">
                {t("needHelpAccessingAccount")}
              </p>
              <a href="#" className="text-violet-400 hover:text-violet-300 text-xs transition-colors">
                {t("contactSupport")}
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} Demo Gateway. {t("allRightsReserved")}
          </p>
        </div>
      </motion.div>
    </div>
  );
}

